import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import LearningPathsTab from './LearningPathsTab';
import UniversalHeader from './UniversalHeader';
import SelectiveDashboard from './selective/SelectiveDashboard';
import SelectiveQuestionBank from './admin/SelectiveQuestionBank';
import BulkQuestionGenerator from './admin/BulkQuestionGenerator';
import WritingPromptManager from './admin/WritingPromptManager';

// Add consistent styling
const addConsistentStyles = () => {
  if (document.getElementById('admin-dashboard-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'admin-dashboard-styles';
  style.textContent = `
    .font-poppins { font-family: 'Poppins', sans-serif; }
    .font-opensans { font-family: 'Open Sans', sans-serif; }
    .text-primary { color: #1E3A8A; }
    .bg-primary { background-color: #1E3A8A; }
    .text-accent { color: #F97316; }
    .bg-accent { background-color: #F97316; }
    .bg-bg-cream { background-color: #FFF7F0; }
    .text-text-dark { color: #333333; }
    .border-primary { border-color: #1E3A8A; }
    .hover\\:bg-primary:hover { background-color: #1E3A8A; }
    .hover\\:text-primary:hover { color: #1E3A8A; }
    .hover\\:bg-orange-600:hover { background-color: #ea580c; }
    .hover\\:bg-blue-800:hover { background-color: #1e40af; }
  `;
  document.head.appendChild(style);
};

const AdminDashboard = ({ onNavigateToQuestions, onNavigateToDashboard, onNavigateToDiagrams }) => {
  const { user, userProfile } = useAuth();
  const [stats, setStats] = useState({
    total_users: 0,
    total_tests: 0,
    recent_tests: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Add consistent styles and fonts
  useEffect(() => {
    addConsistentStyles();
    
    // Add Google Fonts
    if (!document.getElementById('admin-dashboard-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'admin-dashboard-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Open+Sans:wght@400;500;600&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
  }, []);

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin') {
      alert('Access denied. Admin privileges required.');
      window.location.href = '/dashboard';
    }
  }, [userProfile]);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) return;

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/admin/dashboard/stats?user_id=${user.id}`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setStats(data.stats);
          }
        } else if (response.status === 403) {
          alert('Access denied. Admin privileges required.');
          window.location.href = '/dashboard';
        }
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user?.id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="font-opensans text-text-dark bg-bg-cream min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-opensans text-text-dark bg-bg-cream min-h-screen">
      {/* Universal Header */}
      <UniversalHeader
        currentPage="admin"
        onBackToDashboard={onNavigateToDashboard}
        onNavigateToQuestions={onNavigateToQuestions}
        breadcrumbs={[
          { label: 'Dashboard', action: onNavigateToDashboard, current: false },
          { label: 'Admin Panel', action: null, current: true }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Platform Overview
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'questions'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Question Pool Management
            </button>
            <button
              onClick={() => setActiveTab('learning-paths')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'learning-paths'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Learning Paths
            </button>
            <button
              onClick={() => setActiveTab('selective-exam')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'selective-exam'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🎯 Selective Exam Suite
            </button>
            <button
              onClick={() => setActiveTab('question-bank')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'question-bank'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📚 Question Bank
            </button>
            <button
              onClick={() => setActiveTab('bulk-generator')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'bulk-generator'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚡ Bulk Generator
            </button>
            <button
              onClick={() => setActiveTab('writing-prompts')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'writing-prompts'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ✍️ Writing Prompts
            </button>
            {/* AI Diagram Analysis tab temporarily disabled */}
          </nav>
        </div>

        {/* Platform Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Users</p>
                    <p className="text-3xl font-poppins font-bold text-primary">{stats.total_users}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Test Attempts</p>
                    <p className="text-3xl font-poppins font-bold text-green-600">{stats.total_tests}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Avg. Tests per User</p>
                    <p className="text-3xl font-poppins font-bold text-accent">
                      {stats.total_users > 0 ? (stats.total_tests / stats.total_users).toFixed(1) : '0'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Submissions */}
            <div className="bg-white rounded-xl shadow-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-poppins font-semibold text-primary">Recent Test Submissions</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Test Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.recent_tests && stats.recent_tests.map((submission, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {submission.users ? submission.users.email : 'Unknown User'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            submission.test_type === 'Math Proficiency' 
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {submission.test_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`font-medium ${
                            submission.score_percentage >= 80 ? 'text-green-600' :
                            submission.score_percentage >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {submission.score_percentage}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(submission.timestamp)}
                        </td>
                      </tr>
                    ))}
                    {(!stats.recent_tests || stats.recent_tests.length === 0) && (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                          No recent test submissions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Question Pool Management Tab */}
        {activeTab === 'questions' && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <h3 className="text-xl font-poppins font-semibold text-primary mt-4">Question Pool Management</h3>
            <p className="text-gray-600 mt-2">
              Create and manage questions for selective tests
            </p>
            <div className="mt-6">
              <button
                onClick={onNavigateToQuestions}
                className="bg-primary hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105"
              >
                Open Question Pool Manager
              </button>
            </div>
          </div>
        )}

        {/* Learning Paths Tab */}
        {activeTab === 'learning-paths' && (
          <LearningPathsTab />
        )}

        {/* Selective Exam Suite Tab - admin view with admin tools enabled */}
        {activeTab === 'selective-exam' && (
          <SelectiveDashboard isAdmin={true} />
        )}

        {/* Question Bank Tab - Manage Selective Exam Questions */}
        {activeTab === 'question-bank' && (
          <SelectiveQuestionBank />
        )}

        {/* Bulk Question Generator Tab */}
        {activeTab === 'bulk-generator' && (
          <BulkQuestionGenerator />
        )}

        {/* Writing Prompts Manager Tab */}
        {activeTab === 'writing-prompts' && (
          <WritingPromptManager />
        )}

        {/* AI Diagram Analysis Tab - Temporarily Disabled */}
        {/* 
        {activeTab === 'diagrams' && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <span className="text-6xl">🎨</span>
            <h3 className="text-xl font-semibold text-gray-900 mt-4">AI Diagram Analysis Tool</h3>
            <p className="text-gray-600 mt-2">
              Test and analyze automatic diagram generation for math questions
            </p>
            <div className="mt-4 text-sm text-gray-500 max-w-2xl mx-auto">
              <p>
                This advanced tool analyzes math questions and automatically determines when visual diagrams would enhance learning. 
                It supports geometry shapes, number lines, charts, and coordinate grids.
              </p>
            </div>
            <div className="mt-6">
              <button
                onClick={onNavigateToDiagrams}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Open Diagram Analysis Tool
              </button>
            </div>
          </div>
        )}
        */}
      </div>
    </div>
  );
};

export default AdminDashboard;